/**
 * ConventieSSMGenerator - Anexă: Convenție SSM și SU (Situații de Urgență / PSI)
 * Model standard pentru construcții — Legea 319/2006, HG 300/2006, HG 1425/2006, Legea 307/2006
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, HardHat } from "lucide-react";

function generateConventieSSMHTML(d, header) {
  const annexLabel = header?.annex_label || "ANEXA";
  const ctrNo = d.contract_number || "___";
  const clauzeExtra = (d.clauze_suplimentare || "").trim();
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
    body{font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:40px auto;padding:20px;color:#1e293b;line-height:1.65;font-size:12.5px}
    h1{text-align:center;color:#b45309;font-size:17px;margin-bottom:4px;text-transform:uppercase}
    h2{text-align:center;color:#92400e;font-size:13px;font-weight:normal;margin-top:0}
    .header{border-bottom:3px solid #d97706;padding-bottom:15px;margin-bottom:20px}
    .section{margin-bottom:14px}
    .section-title{font-weight:bold;color:#b45309;border-bottom:1px solid #fde68a;padding-bottom:4px;margin-bottom:8px;font-size:13px}
    table{width:100%;border-collapse:collapse;margin:8px 0}
    td{padding:6px 10px;border:1px solid #d1d5db;vertical-align:top}
    td:first-child{width:35%;background:#fffbeb;font-weight:600;color:#92400e}
    ol{margin:6px 0;padding-left:22px}
    ol li{margin-bottom:5px;text-align:justify}
    .signatures{display:flex;justify-content:space-between;margin-top:40px;page-break-inside:avoid}
    .sig-box{width:45%;text-align:center;border-top:2px solid #d97706;padding-top:10px}
    .sig-box p{margin:4px 0;font-size:12px}
    .footer{text-align:center;margin-top:30px;font-size:11px;color:#64748b;border-top:1px solid #e2e8f0;padding-top:10px}
    .legal{background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:8px 12px;font-size:11.5px;color:#78350f}
  </style></head><body>
  <div class="header">
    <h1>Convenție privind Securitatea și Sănătatea în Muncă (SSM)<br>și Situațiile de Urgență (SU / PSI)</h1>
    <h2>${annexLabel} CTR ${ctrNo} — anexă și parte integrantă la Contractul nr. ${ctrNo} din ${d.contract_date || "___"}</h2>
  </div>

  <div class="section">
    <div class="section-title">CAP. I — Părțile convenției</div>
    <table>
      <tr><td>BENEFICIAR</td><td>${d.beneficiar_name || "___"}, CUI: ${d.beneficiar_cui || "___"}, sediul: ${d.beneficiar_address || "___"}, reprezentată prin ${d.beneficiar_reprezentant || "___"} (${d.beneficiar_functie || "reprezentant legal"})</td></tr>
      <tr><td>EXECUTANT</td><td>${d.executant_name || "___"}, CUI: ${d.executant_cui || "___"}, sediul: ${d.executant_address || "___"}, reprezentată prin ${d.executant_reprezentant || "___"} (${d.executant_functie || "reprezentant legal"})</td></tr>
      <tr><td>Responsabil SSM Executant</td><td>${d.responsabil_ssm_executant || "___"}</td></tr>
      <tr><td>Responsabil SSM Beneficiar</td><td>${d.responsabil_ssm_beneficiar || "___"}</td></tr>
    </table>
  </div>

  <div class="section">
    <div class="section-title">CAP. II — Obiectul convenției</div>
    <p style="text-align:justify">Prezenta convenție stabilește responsabilitățile și obligațiile părților privind securitatea și sănătatea în muncă (SSM) și situațiile de urgență (SU/PSI) pe durata executării lucrărilor de <b>${d.obiect_lucrare || "___"}</b>, la punctul de lucru: <b>${d.locatie_lucrare || "___"}</b>, în conformitate cu Contractul nr. ${ctrNo} din ${d.contract_date || "___"}.</p>
  </div>

  <div class="section">
    <div class="section-title">CAP. III — Temei legal</div>
    <div class="legal">
      • Legea nr. 319/2006 a securității și sănătății în muncă, cu modificările și completările ulterioare;<br>
      • H.G. nr. 1425/2006 — Normele metodologice de aplicare a Legii 319/2006 (modificată prin H.G. 955/2010);<br>
      • H.G. nr. 300/2006 — cerințe minime de securitate și sănătate pentru șantierele temporare sau mobile;<br>
      • H.G. nr. 1146/2006 — cerințe minime pentru utilizarea în muncă a echipamentelor de muncă;<br>
      • H.G. nr. 1048/2006 — cerințe minime privind echipamentul individual de protecție (EIP);<br>
      • Legea nr. 307/2006 privind apărarea împotriva incendiilor, cu modificările ulterioare;<br>
      • Legea nr. 53/2003 — Codul Muncii, republicat.
    </div>
  </div>

  <div class="section">
    <div class="section-title">CAP. IV — Obligațiile Executantului</div>
    <ol>
      <li>Să respecte și să aplice, prin personalul propriu și al eventualilor subcontractanți, toate prevederile legislației SSM în vigoare, pe întreaga durată a executării lucrărilor.</li>
      <li>Să efectueze instruirea personalului propriu (instructaj introductiv-general, la locul de muncă și periodic) asupra tehnologiei de execuție, riscurilor specifice și normelor SSM și SU, consemnată în fișele individuale de instruire, semnate de fiecare lucrător.</li>
      <li>Să efectueze evaluarea riscurilor de accidentare și îmbolnăvire profesională pentru locurile de muncă și activitățile proprii desfășurate în șantier.</li>
      <li>Să elaboreze Planul propriu de securitate și sănătate (conform H.G. 300/2006) și să îl pună la dispoziția Beneficiarului și/sau a coordonatorului SSM al șantierului, armonizat cu Planul general de securitate și sănătate al șantierului.</li>
      <li>Să acorde personalului propriu echipamentul individual de protecție (EIP) adecvat riscurilor identificate (cască, încălțăminte de protecție, vestă reflectorizantă, ham de siguranță la lucrul la înălțime etc.) și să asigure purtarea obligatorie a acestuia.</li>
      <li>Să utilizeze numai echipamente de muncă, scule și utilaje conforme, verificate tehnic (inclusiv ISCIR unde este cazul), deservite de personal calificat și autorizat.</li>
      <li>Să delimiteze și să semnalizeze corespunzător zona de lucru, traseele de acces și zonele de depozitare a materialelor, fără a afecta căile de evacuare și accesul mijloacelor de intervenție.</li>
      <li>Să asigure securitatea șantierului și a zonei de lucru pe perioada opririi sau sistării lucrărilor, răspunderea aparținând exclusiv Executantului.</li>
      <li>Să permită accesul în zona de lucru numai personalului propriu instruit și apt medical (fișe de aptitudine medicina muncii valabile).</li>
      <li>Să comunice imediat Beneficiarului orice eveniment, accident de muncă sau incident periculos produs în zona de lucru și să participe la cercetarea acestuia, conform Legii 319/2006.</li>
      <li>Să răspundă de respectarea prezentei convenții de către toți subcontractanții săi, menținând o listă actualizată a acestora.</li>
      <li>Să mențină ordinea și curățenia la locul de muncă și să evacueze ritmic deșeurile rezultate din activitatea proprie.</li>
    </ol>
  </div>

  <div class="section">
    <div class="section-title">CAP. V — Obligațiile Beneficiarului</div>
    <ol>
      <li>Să predea amplasamentul Executantului pe bază de proces-verbal de predare-primire și să îl informeze asupra riscurilor specifice existente în incintă / la punctul de lucru.</li>
      <li>Să efectueze instruirea colectivă de SSM și SU la intrarea în unitate/șantier a personalului Executantului, specifică lucrărilor contractate, consemnată în fișa de instruire colectivă.</li>
      <li>Să pună la dispoziția Executantului informațiile privind modul de alarmare, evacuare și primă intervenție aplicabile la punctul de lucru.</li>
      <li>Să asigure coordonarea aplicării principiilor generale de prevenire, inclusiv prin coordonatorul SSM al șantierului, acolo unde este cazul (H.G. 300/2006).</li>
      <li>Să aibă dreptul de a inspecta, în orice moment, modul în care Executantul respectă legislația SSM/SU și prezenta convenție, putând dispune oprirea lucrărilor în caz de pericol grav și iminent.</li>
      <li>Să comunice Executantului orice modificare a condițiilor de lucru din incintă care poate genera riscuri noi pentru personalul acestuia.</li>
    </ol>
  </div>

  <div class="section">
    <div class="section-title">CAP. VI — Situații de Urgență / Apărarea împotriva incendiilor (PSI)</div>
    <ol>
      <li>Executantul va respecta prevederile Legii 307/2006 și normele de apărare împotriva incendiilor aplicabile la punctul de lucru.</li>
      <li>Lucrările cu foc deschis (sudură, tăiere, lipire etc.) se vor executa numai pe baza permisului de lucru cu foc, emis conform reglementărilor în vigoare, cu asigurarea măsurilor de prevenire și a mijloacelor de primă intervenție.</li>
      <li>Executantul va dota zona proprie de lucru cu mijloace de primă intervenție (stingătoare adecvate) și va instrui personalul asupra utilizării acestora.</li>
      <li>Este interzisă blocarea căilor de acces și evacuare, a hidranților și a mijloacelor de intervenție din incinta Beneficiarului.</li>
      <li>Fumatul este permis numai în locurile special amenajate și marcate corespunzător.</li>
      <li>În caz de incendiu sau altă situație de urgență, personalul Executantului va acționa conform planului de alarmare/evacuare comunicat de Beneficiar și va anunța imediat serviciile de urgență (112) și reprezentantul Beneficiarului.</li>
    </ol>
  </div>

  <div class="section">
    <div class="section-title">CAP. VII — Răspunderi</div>
    <ol>
      <li>Executantul răspunde unilateral, în fața legii, de consecințele produse (accidente de muncă, avarii, incendii, pagube materiale) ca urmare a nerespectării normelor SSM și SU de către personalul propriu sau al subcontractanților săi.</li>
      <li>Accidentele de muncă suferite de personalul Executantului se înregistrează și se cercetează de către Executant, cu informarea imediată a Beneficiarului, conform Legii 319/2006 și H.G. 1425/2006.</li>
      <li>Fiecare parte răspunde pentru personalul propriu în privința instruirii, dotării cu EIP, supravegherii medicale și disciplinei în muncă.</li>
      <li>Pagubele produse de o parte celeilalte părți din culpa proprie se suportă de partea vinovată.</li>
    </ol>
  </div>

  ${clauzeExtra ? `<div class="section">
    <div class="section-title">CAP. VIII — Clauze suplimentare</div>
    <p style="text-align:justify;white-space:pre-line">${clauzeExtra}</p>
  </div>` : ""}

  <div class="section">
    <div class="section-title">CAP. ${clauzeExtra ? "IX" : "VIII"} — Dispoziții finale</div>
    <ol>
      <li>Prezenta convenție este anexă și parte integrantă a Contractului nr. ${ctrNo} din ${d.contract_date || "___"} și este valabilă pe toată durata executării lucrărilor, de la predarea amplasamentului până la recepția la terminarea lucrărilor.</li>
      <li>Modificarea prezentei convenții se poate face numai prin act adițional semnat de ambele părți.</li>
      <li>Prezenta convenție a fost încheiată astăzi, ${d.data_conventie || "___"}, în 2 (două) exemplare originale, câte unul pentru fiecare parte.</li>
    </ol>
  </div>

  <div class="signatures">
    <div class="sig-box">
      <p><strong>BENEFICIAR</strong></p>
      <p>${d.beneficiar_name || "___"}</p>
      <p>${d.beneficiar_reprezentant || "___"}</p>
      <p style="margin-top:30px">(semnătura și ștampila)</p>
    </div>
    <div class="sig-box">
      <p><strong>EXECUTANT</strong></p>
      <p>${d.executant_name || "___"}</p>
      <p>${d.executant_reprezentant || "___"}</p>
      <p style="margin-top:30px">(semnătura și ștampila)</p>
    </div>
  </div>
  <div class="footer">Document generat de <a href="https://e-tesa.ro" target="_blank" style="color: #1d4ed8; text-decoration: underline; font-weight: 500;">e-Tesa</a> — ${new Date().toLocaleDateString("ro-RO")}</div>
  </body></html>`;
}

export default function ConventieSSMGenerator({ contractData, existingAnnex, onSave, onCancel }) {
  const existing = existingAnnex?.data || {};

  const [form, setForm] = useState({
    contract_number: existing.contract_number || contractData.contract_number || "",
    contract_date: existing.contract_date || contractData.contract_date || "",
    executant_name: existing.executant_name || contractData.executant_name || "",
    executant_cui: existing.executant_cui || contractData.executant_cui || "",
    executant_address: existing.executant_address || contractData.executant_address || "",
    executant_reprezentant: existing.executant_reprezentant || contractData.executant_reprezentant || "",
    executant_functie: existing.executant_functie || contractData.executant_functie || "",
    beneficiar_name: existing.beneficiar_name || contractData.beneficiar_name || "",
    beneficiar_cui: existing.beneficiar_cui || contractData.beneficiar_cui || "",
    beneficiar_address: existing.beneficiar_address || contractData.beneficiar_address || "",
    beneficiar_reprezentant: existing.beneficiar_reprezentant || contractData.beneficiar_reprezentant || "",
    beneficiar_functie: existing.beneficiar_functie || contractData.beneficiar_functie || "",
    obiect_lucrare: existing.obiect_lucrare || contractData.obiect_lucrare || "",
    locatie_lucrare: existing.locatie_lucrare || contractData.locatie_lucrare || "",
    responsabil_ssm_executant: existing.responsabil_ssm_executant || contractData.executant_reprezentant || "",
    responsabil_ssm_beneficiar: existing.responsabil_ssm_beneficiar || contractData.beneficiar_reprezentant || "",
    data_conventie: existing.data_conventie || new Date().toISOString().slice(0, 10),
    clauze_suplimentare: existing.clauze_suplimentare || "",
  });

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = () => {
    const header = { annex_label: existingAnnex?.annex_label || "ANEXA" };
    const html = generateConventieSSMHTML(form, header);
    onSave({
      annex_type: "conventie_ssm",
      data: form,
      html_content: html,
      title: "Convenție SSM și SU",
    });
  };

  return (
    <div className="space-y-4" data-testid="conventie-ssm-editor">
      <Card className="border-0 shadow-lg border-t-4 border-t-amber-500">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onCancel} className="p-1 h-auto" data-testid="cancel-conventie-ssm-top"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <HardHat className="w-5 h-5 text-amber-600" />
                {existingAnnex?.annex_label || "ANEXA"} — Convenție SSM și SU (PSI)
              </CardTitle>
              <CardDescription>
                Model construcții — Legea 319/2006, HG 300/2006, HG 1425/2006, Legea 307/2006. Clauzele standard sunt incluse automat în document.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Executant */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-amber-50 rounded-lg">
            <h3 className="col-span-2 font-semibold text-amber-800">Executant</h3>
            <div><Label>Nume firmă</Label><Input value={form.executant_name} onChange={(e) => upd("executant_name", e.target.value)} data-testid="ssm-executant-name" /></div>
            <div><Label>CUI</Label><Input value={form.executant_cui} onChange={(e) => upd("executant_cui", e.target.value)} /></div>
            <div className="col-span-2"><Label>Adresă</Label><Input value={form.executant_address} onChange={(e) => upd("executant_address", e.target.value)} /></div>
            <div><Label>Reprezentant</Label><Input value={form.executant_reprezentant} onChange={(e) => upd("executant_reprezentant", e.target.value)} /></div>
            <div><Label>Funcție</Label><Input value={form.executant_functie} onChange={(e) => upd("executant_functie", e.target.value)} /></div>
            <div className="col-span-2"><Label>Responsabil SSM Executant</Label><Input value={form.responsabil_ssm_executant} onChange={(e) => upd("responsabil_ssm_executant", e.target.value)} placeholder="Nume responsabil / lucrător desemnat SSM" /></div>
          </div>

          {/* Beneficiar */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
            <h3 className="col-span-2 font-semibold text-slate-800">Beneficiar</h3>
            <div><Label>Nume firmă</Label><Input value={form.beneficiar_name} onChange={(e) => upd("beneficiar_name", e.target.value)} data-testid="ssm-beneficiar-name" /></div>
            <div><Label>CUI</Label><Input value={form.beneficiar_cui} onChange={(e) => upd("beneficiar_cui", e.target.value)} /></div>
            <div className="col-span-2"><Label>Adresă</Label><Input value={form.beneficiar_address} onChange={(e) => upd("beneficiar_address", e.target.value)} /></div>
            <div><Label>Reprezentant</Label><Input value={form.beneficiar_reprezentant} onChange={(e) => upd("beneficiar_reprezentant", e.target.value)} /></div>
            <div><Label>Funcție</Label><Input value={form.beneficiar_functie} onChange={(e) => upd("beneficiar_functie", e.target.value)} /></div>
            <div className="col-span-2"><Label>Responsabil SSM Beneficiar</Label><Input value={form.responsabil_ssm_beneficiar} onChange={(e) => upd("responsabil_ssm_beneficiar", e.target.value)} placeholder="Nume responsabil SSM al beneficiarului" /></div>
          </div>

          {/* Contract & lucrare */}
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Nr. contract</Label><Input value={form.contract_number} onChange={(e) => upd("contract_number", e.target.value)} /></div>
            <div><Label>Data contract</Label><Input type="date" value={form.contract_date} onChange={(e) => upd("contract_date", e.target.value)} /></div>
            <div className="col-span-2"><Label>Obiect lucrare</Label><Input value={form.obiect_lucrare} onChange={(e) => upd("obiect_lucrare", e.target.value)} /></div>
            <div><Label>Locație lucrare / punct de lucru</Label><Input value={form.locatie_lucrare} onChange={(e) => upd("locatie_lucrare", e.target.value)} /></div>
            <div><Label>Data încheierii convenției</Label><Input type="date" value={form.data_conventie} onChange={(e) => upd("data_conventie", e.target.value)} /></div>
          </div>

          {/* Clauze suplimentare */}
          <div>
            <Label>Clauze suplimentare (opțional)</Label>
            <Textarea
              value={form.clauze_suplimentare}
              onChange={(e) => upd("clauze_suplimentare", e.target.value)}
              rows={3}
              placeholder="Clauze specifice șantierului: lucru la înălțime, spații închise, program de lucru, echipamente speciale..."
              data-testid="ssm-clauze-suplimentare"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>Anulează</Button>
            <Button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700" data-testid="save-conventie-ssm">
              <Save className="w-4 h-4 mr-2" />Salvează {existingAnnex?.annex_label || "ANEXA"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
